import type { reploOpencodeHarnessToolIconKeyByName } from "schemas/agentTools/reploOpencodeHarness";
import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";
import type { OpencodeToolDisplay, ToolDisplayText } from "./shared";

import {
  getFilePathDisplay,
  getFilePathSummaryDisplay,
  getToolDisplayText,
  parseRecordOrNull,
} from "./shared";

type ReploOpencodeHarnessToolName =
  keyof typeof reploOpencodeHarnessToolIconKeyByName;

const REPLO_OPENCODE_HARNESS_TEXT_BY_TOOL_NAME = {
  "capture-html": getToolDisplayText({
    completed: "Read page",
    pending: "Reading page",
    running: "Reading page",
  }),
  "compile-email": getToolDisplayText({
    completed: "Compiled email",
    pending: "Compiling email",
    running: "Compiling email",
  }),
  "publish-email": getToolDisplayText({
    completed: "Published email",
    pending: "Publishing email",
    running: "Publishing email",
  }),
  "create-registry-preview": getToolDisplayText({
    completed: "Prepared registry preview",
    pending: "Preparing registry preview",
    running: "Preparing registry preview",
  }),
  "dev-control": getToolDisplayText({
    completed: "Updated preview",
    pending: "Updating preview",
    running: "Updating preview",
  }),
  "find-registry-items": getToolDisplayText({
    completed: "Searched registry",
    pending: "Searching registry",
    running: "Searching registry",
  }),
  "find-design-inspiration": getToolDisplayText({
    completed: "Searched design inspiration",
    pending: "Searching design inspiration",
    running: "Searching design inspiration",
  }),
  "add-registry-items": getToolDisplayText({
    completed: "Added registry items",
    pending: "Adding registry items",
    running: "Adding registry items",
  }),
  "download-registry-items": getToolDisplayText({
    completed: "Downloaded registry items",
    pending: "Downloading registry items",
    running: "Downloading registry items",
  }),
  "install-staged-registry-items": getToolDisplayText({
    completed: "Installed registry items",
    pending: "Installing registry items",
    running: "Installing registry items",
  }),
  "manage-tabs": getToolDisplayText({
    completed: "Managed tabs",
    pending: "Managing tabs",
    running: "Managing tabs",
  }),
  publish: getToolDisplayText({
    completed: "Published site",
    pending: "Publishing site",
    running: "Publishing site",
  }),
  "collect-site-report-evidence": getToolDisplayText({
    completed: "Read a page",
    pending: "Reading a page",
    running: "Reading a page",
  }),
  "page-audit": getToolDisplayText({
    completed: "Checked the page",
    error: "Could not check the page",
    pending: "Checking the page",
    running: "Checking the page",
  }),
  "show-gallery": getToolDisplayText({
    completed: "Showed examples",
    pending: "Gathering examples",
    running: "Gathering examples",
  }),
  "suggest-followups": getToolDisplayText({
    completed: "Suggested next steps",
    pending: "Suggesting next steps",
    running: "Suggesting next steps",
  }),
  "upload-registry-items": getToolDisplayText({
    completed: "Uploaded registry items",
    pending: "Uploading registry items",
    running: "Uploading registry items",
  }),
  "upload-asset": getToolDisplayText({
    completed: "Uploaded file",
    pending: "Uploading file",
    running: "Uploading file",
  }),
  "upload-assets": getToolDisplayText({
    completed: "Uploaded files",
    pending: "Uploading files",
    running: "Uploading files",
  }),
  "validate-insights-report": getToolDisplayText({
    completed: "Validated report",
    pending: "Validating report",
    running: "Validating report",
  }),
  "v1-migration-check": getToolDisplayText({
    completed: "Checked migration",
    pending: "Checking migration",
    running: "Checking migration",
  }),
  "import-figma-page": getToolDisplayText({
    completed: "Imported Figma page",
    error: "Figma import",
    pending: "Importing Figma page",
    running: "Importing Figma page",
  }),
  "import-site-page": getToolDisplayText({
    completed: "Imported page",
    error: "Fast import unavailable",
    pending: "Importing page",
    running: "Importing page",
  }),
  "get-branding": getToolDisplayText({
    completed: "Read brand",
    pending: "Reading brand",
    running: "Reading brand",
  }),
  "set-branding": getToolDisplayText({
    completed: "Updated brand",
    pending: "Updating brand",
    running: "Updating brand",
  }),
  "set-branding-file": getToolDisplayText({
    completed: "Updated brand context files",
    pending: "Updating brand context files",
    running: "Updating brand context files",
  }),
  "update-brand": getToolDisplayText({
    completed: "Updated brand",
    pending: "Updating brand",
    running: "Updating brand",
  }),
  "share-image": getToolDisplayText({
    completed: "Shared image",
    pending: "Sharing image",
    running: "Sharing image",
  }),
  "extract-branding": getToolDisplayText({
    completed: "Extracted brand",
    pending: "Extracting brand",
    running: "Extracting brand",
  }),
} satisfies Record<ReploOpencodeHarnessToolName, ToolDisplayText>;

const REPLO_OPENCODE_HARNESS_FILE_PATH_TOOL_NAMES: ReadonlySet<ReploOpencodeHarnessToolName> =
  new Set(["upload-asset"]);

export function getReploOpencodeHarnessToolDisplay({
  toolName,
  toolPart,
}: {
  toolName: ReploOpencodeHarnessToolName;
  toolPart: OpencodeToolPart;
}): OpencodeToolDisplay {
  const summary = REPLO_OPENCODE_HARNESS_TEXT_BY_TOOL_NAME[toolName];
  if (toolName === "import-figma-page") {
    // The import reads the frame through the project's Figma connection, so its row wears Figma's logo like the
    // Figma calls around it.
    return { summary, detail: null, integrationKey: "figma" };
  }
  if (toolName === "upload-assets") {
    return getUploadAssetsDisplay({ summary, toolPart });
  }
  return REPLO_OPENCODE_HARNESS_FILE_PATH_TOOL_NAMES.has(toolName)
    ? getFilePathDisplay({ prefix: summary, toolPart })
    : { summary, detail: null };
}

/** One local file shows its path, like the file tools; a batch keeps the plural summary. */
function getUploadAssetsDisplay({
  summary,
  toolPart,
}: {
  summary: ToolDisplayText;
  toolPart: OpencodeToolPart;
}) {
  const assets = toolPart.state.input.assets;
  const onlyAsset =
    Array.isArray(assets) && assets.length === 1
      ? parseRecordOrNull(assets[0])
      : null;
  if (typeof onlyAsset?.filePath !== "string") {
    return { summary, detail: null };
  }
  return getFilePathSummaryDisplay({
    prefix: REPLO_OPENCODE_HARNESS_TEXT_BY_TOOL_NAME["upload-asset"],
    filePath: onlyAsset.filePath,
  });
}
